/**
 * @fileoverview Venues management page component.
 * @summary Page for organizers to manage their karaoke venues.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/VenuesPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { useAppDispatch, useAppSelector } from '../../hooks/redux.js'
import { fetchVenues, createVenue, updateVenue, deleteVenue, type Venue } from '../../store/slices/venuesSlice.js'
import Spinner from '../../components/Spinner.js'
import PageHeader from '../../components/PageHeader.js'
import { ConfirmModal } from '../../components/ConfirmModal.js'
import { VenueForm } from './components/VenueForm.js'
import { VenuesList } from './components/VenuesList.js'

/**
 * @summary Venues management page component.
 * @returns {React.ReactElement} Venues page.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function VenuesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const { venues, isLoading, error: venuesError } = useAppSelector((state) => state.venues)

  const [showForm, setShowForm] = useState(false)
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    googleMapsUrl: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Venue | null>(null)

  // Redirect if not authenticated or not organizer
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    if (user.role !== 'organizer' && user.role !== 'superadmin') {
      navigate('/', { replace: true })
      return
    }
  }, [user, navigate])

  // Fetch venues on mount
  useEffect(() => {
    if (user && (user.role === 'organizer' || user.role === 'superadmin')) {
      dispatch(fetchVenues())
    }
  }, [dispatch, user])

  const generateGoogleMapsUrl = () => {
    const parts: string[] = []
    
    // Add name first if available
    if (formData.name.trim()) {
      parts.push(formData.name.trim())
    }
    
    // Add address if available
    if (formData.address.trim()) {
      parts.push(formData.address.trim())
    }
    
    // Add city (required for search)
    if (formData.city.trim()) {
      parts.push(formData.city.trim())
    }
    
    // Add country if available
    if (formData.country.trim()) {
      parts.push(formData.country.trim())
    }
    
    // Need at least city or name to generate a useful link
    if (parts.length === 0) {
      setError(t('venues.generateMapsUrlError'))
      return
    }
    
    const query = parts.join(', ')
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    setFormData({ ...formData, googleMapsUrl: mapsUrl })
  }

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      country: '',
      googleMapsUrl: '',
    })
    setEditingVenue(null)
    setShowForm(false)
    setError(null)
    setSuccess(null)
  }

  const handleEdit = (venue: Venue) => {
    setEditingVenue(venue)
    setFormData({
      name: venue.name,
      address: venue.address || '',
      city: venue.city,
      country: venue.country || '',
      googleMapsUrl: venue.googleMapsUrl || '',
    })
    setShowForm(true)
    setError(null)
    setSuccess(null)
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const venueData: {
        name: string
        address?: string
        city: string
        country?: string
        googleMapsUrl?: string
      } = {
        name: formData.name.trim(),
        city: formData.city.trim(),
      }

      // Add optional fields if provided
      if (formData.address.trim()) {
        venueData.address = formData.address.trim()
      }
      if (formData.country.trim()) {
        venueData.country = formData.country.trim()
      }
      if (formData.googleMapsUrl.trim()) {
        venueData.googleMapsUrl = formData.googleMapsUrl.trim()
      }

      if (editingVenue) {
        await dispatch(updateVenue({ venueId: editingVenue.id, venueData })).unwrap()
        setSuccess(t('venues.updateSuccess'))
      } else {
        await dispatch(createVenue(venueData)).unwrap()
        setSuccess(t('venues.addSuccess'))
      }

      resetForm()
      await dispatch(fetchVenues())
    } catch (err: any) {
      setError(
        err.message ||
          (editingVenue ? t('venues.updateError') : t('venues.addError')),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirmDelete) return

    setIsDeleting(confirmDelete.id)
    setError(null)
    setSuccess(null)

    try {
      await dispatch(deleteVenue(confirmDelete.id)).unwrap()
      setSuccess(t('venues.deleteSuccess'))
      setConfirmDelete(null)
      await dispatch(fetchVenues())
    } catch (err: any) {
      setError(err.message || t('venues.deleteError'))
      setConfirmDelete(null)
    } finally {
      setIsDeleting(null)
    }
  }

  if (isLoading && venues.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="w-full page-container">
        {/* Header */}
        <PageHeader
          title={t('venues.title')}
          subtitle={t('venues.subtitle')}
          className="mb-8"
        >
          {!showForm && (
            <button
              onClick={() => {
                resetForm()
                setShowForm(true)
              }}
              className="btn btn-primary"
            >
              <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
              {t('venues.addVenue')}
            </button>
          )}
        </PageHeader>

        {/* Success/Error messages */}
        {success && (
          <div className="alert alert-success mb-6 flex items-center gap-2">
            <span>{success}</span>
          </div>
        )}
        {(error || venuesError) && (
          <div className="alert alert-error mb-6 flex items-center gap-2">
            <span>{error || venuesError}</span>
          </div>
        )}

        {/* Venue Form */}
        <VenueForm
          isOpen={showForm}
          editingVenue={editingVenue}
          formData={formData}
          isSaving={isSaving}
          error={error}
          onFormDataChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
          onGenerateMapsUrl={generateGoogleMapsUrl}
          onSubmit={handleSubmit}
          onCancel={resetForm}
        />

        {/* Venues List */}
        <VenuesList
          venues={venues}
          isDeleting={isDeleting}
          onEdit={handleEdit}
          onDelete={(venue) => setConfirmDelete(venue)}
          onCreate={() => {
            resetForm()
            setShowForm(true)
          }}
        />

        {/* Confirm Delete Modal */}
        <ConfirmModal
          isOpen={!!confirmDelete}
          title={t('venues.confirmDeleteTitle')}
          message={t('venues.confirmDelete').replace('{{name}}', confirmDelete?.name || '')}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
          isLoading={confirmDelete ? isDeleting === confirmDelete.id : false}
        />

      </div>
    </div>
  )
}

